import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (
      currentHost === 'localhost'
      || currentHost === '127.0.0.1'
      || currentHost === '::1'
      || currentHost === '[::1]'
    ) {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const OwnerApplicationPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [form, setForm] = useState({
    requested_company_name: '',
    requested_company_slug: '',
    business_email: '',
    business_phone: '',
    legal_business_name: '',
    business_address: '',
    website_url: '',
    years_in_business: '',
    tax_identifier_last4: '',
    message: '',
  });
  const [application, setApplication] = useState(null);
  const [eligible, setEligible] = useState(true);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true, state: { from: '/owner-application' } });
      return;
    }

    const loadCurrentApplication = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(apiUrl('userprofiles/owner-applications/me/'), authHeaders);
        const payload = response.data || {};
        setApplication(payload.application || null);
        setEligible(Boolean(payload.eligible));
      } catch (error) {
        setFeedback({ type: 'error', message: 'Unable to load owner-application status.' });
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrentApplication();
  }, [token, navigate, authHeaders]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setFeedback({ type: '', message: '' });
      setIsSubmitting(true);
      const response = await axios.post(
        apiUrl('userprofiles/owner-applications/submit/'),
        {
          ...form,
          years_in_business: form.years_in_business === '' ? null : Number(form.years_in_business),
        },
        authHeaders,
      );
      setApplication(response.data);
      setFeedback({ type: 'success', message: 'Owner application submitted successfully.' });
    } catch (error) {
      const apiError = error?.response?.data?.error;
      setFeedback({
        type: 'error',
        message: (typeof apiError === 'string' && apiError.trim())
          ? apiError
          : 'Unable to submit owner application right now.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1F8] px-3 py-4 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#4C1D95] via-[#6D28D9] to-[#7C3AED] px-4 py-6 text-white shadow-xl sm:px-6 sm:py-8">
          <h1 className="text-2xl font-bold sm:text-3xl">Owner Access Application</h1>
          <p className="mt-2 text-sm text-purple-100">
            Apply for owner permissions to manage your company workspace, workforce, and brand settings.
          </p>
        </header>

        <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {feedback.message ? <InlineFeedback type={feedback.type || 'info'} message={feedback.message} /> : null}
          {isLoading ? <InlineFeedback type="info" message="Loading application status..." /> : null}

          {!isLoading && application ? (
            <div className="rounded-xl border border-[#E7DAFF] bg-[#FAF7FF] p-4 text-sm space-y-1">
              <p><strong>Status:</strong> {application.status}</p>
              <p><strong>Company:</strong> {application.requested_company_name}</p>
              <p><strong>Business Email:</strong> {application.business_email}</p>
              {application.review_note ? <p><strong>Reviewer Note:</strong> {application.review_note}</p> : null}
            </div>
          ) : null}

          {!isLoading && !eligible ? (
            <InlineFeedback type="warning" message="Your current role cannot submit an owner application." />
          ) : null}

          {!isLoading && eligible && (!application || application.status === 'rejected') ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="requested_company_name" className="text-sm font-semibold text-gray-700">Company Name</label>
                <input
                  id="requested_company_name"
                  type="text"
                  required
                  value={form.requested_company_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, requested_company_name: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label htmlFor="requested_company_slug" className="text-sm font-semibold text-gray-700">Preferred Company Slug (optional)</label>
                <input
                  id="requested_company_slug"
                  type="text"
                  value={form.requested_company_slug}
                  onChange={(e) => setForm((prev) => ({ ...prev, requested_company_slug: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="example-company"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="business_email" className="text-sm font-semibold text-gray-700">Business Email</label>
                  <input
                    id="business_email"
                    type="email"
                    required
                    value={form.business_email}
                    onChange={(e) => setForm((prev) => ({ ...prev, business_email: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="business_phone" className="text-sm font-semibold text-gray-700">Business Phone (optional)</label>
                  <input
                    id="business_phone"
                    type="text"
                    value={form.business_phone}
                    onChange={(e) => setForm((prev) => ({ ...prev, business_phone: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="legal_business_name" className="text-sm font-semibold text-gray-700">Legal Business Name</label>
                  <input
                    id="legal_business_name"
                    type="text"
                    value={form.legal_business_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, legal_business_name: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="years_in_business" className="text-sm font-semibold text-gray-700">Years in Business</label>
                  <input
                    id="years_in_business"
                    type="number"
                    min={0}
                    max={80}
                    value={form.years_in_business}
                    onChange={(e) => setForm((prev) => ({ ...prev, years_in_business: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="business_address" className="text-sm font-semibold text-gray-700">Business Address</label>
                  <input
                    id="business_address"
                    type="text"
                    value={form.business_address}
                    onChange={(e) => setForm((prev) => ({ ...prev, business_address: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="website_url" className="text-sm font-semibold text-gray-700">Website URL</label>
                  <input
                    id="website_url"
                    type="url"
                    value={form.website_url}
                    onChange={(e) => setForm((prev) => ({ ...prev, website_url: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tax_identifier_last4" className="text-sm font-semibold text-gray-700">Tax Identifier Last 4 (optional)</label>
                <input
                  id="tax_identifier_last4"
                  type="text"
                  maxLength={4}
                  value={form.tax_identifier_last4}
                  onChange={(e) => setForm((prev) => ({ ...prev, tax_identifier_last4: e.target.value.replace(/[^0-9]/g, '') }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label htmlFor="message" className="text-sm font-semibold text-gray-700">Application Note (optional)</label>
                <textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  rows={4}
                  placeholder="Share business background, team size, or launch goals."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-full bg-[#4C1D95] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#3A1774] disabled:opacity-60"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Owner Application'}
              </button>
            </form>
          ) : null}

          <div className="pt-2 text-sm">
            <Link to="/profile" className="text-[#4C1D95] hover:underline">Back to profile</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerApplicationPage;
